import { createSlice } from '@reduxjs/toolkit';
import {
  fetchSideWallStageOneForm,
  fetchSideWallStageThreeForm,
  deleteSideWallStageOne,
  addSideWallStageOne,
  fetchSideWallStageOneList,
  addSideWallStageThree,
  fetchSideWallStageThreeList,
  deleteSideWallStageThree,
  fetchSideWallStageTwoForm,
  fetchmetaltypeFrameNumbers,
  fetchSideWallStageTwoList,
  addSideWallStageTwo,
  fetchSideWallFrameNumbers,
  fetchSideWallDrawingNumbers,
  sideWallActionStageOne,
  fetchSideWallFlowHistory,
  fetchViewSideWallStageOne,
  fetchViewSideWallStageTwo,
  fetchViewSideWallStageThree,
  fetchSideWallFlowHistoryStageOne,
  fetchSideWallFlowHistoryStageTwo,
  fetchSideWallFlowHistoryStageThree,
} from './sideWallThunk';

const initialState = {
  sideWallStageOne: null,
  sideWallStageThree: null, // Add this for component to use
  stageThreeFormData: null,
  stageTwoData: null,
  stageTwoList: [],
  pagination: [],
  metalframeNumbers: [],
  sideWallFrameNumbers: [],
  sideWallDrawingNumbers: [],
  stageOneList: [],
  flowHistory: [],
  flowHistoryStageOne: [],
  flowHistoryStageThree: [],
  loading: false,
  error: null,
  successMessage: null,
  addStageOne: { loading: false, success: false, error: null },
  deleteStageOne: { loading: false, success: false, error: null },
  addStageTwo: { loading: false, success: false, error: null },
  deleteStageTwo: { loading: false, success: false, error: null },
  stageThreeList: [],
  stageThreePagination: {},

  addStageThree: {
    loading: false,
    success: false,
    error: null,
  },
  updateStageThree: {
    loading: false,
    success: false,
    error: null,
  },
  deleteStageThree: {
    loading: false,
    success: false,
    error: null,
  },
  viewStageOne: {
    loading: false,
    success: false,
    error: null,
    data: null,
  },
  viewStageTwo: {
    loading: false,
    success: false,
    error: null,
    data: null,
  },
  viewStageThree: {
    loading: false,
    success: false,
    error: null,
    data: null,
  },
};

const sideWallSlice = createSlice({
  name: 'sideWall',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchSideWallDrawingNumbers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSideWallDrawingNumbers.fulfilled, (state, action) => {
        state.loading = false;
        state.sideWallDrawingNumbers = action.payload.data;
      })
      .addCase(fetchSideWallDrawingNumbers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('Failed to load drawing numbers:', action.payload);
        state.sideWallDrawingNumbers = [];
      })
      .addCase(fetchSideWallFrameNumbers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSideWallFrameNumbers.fulfilled, (state, action) => {
        state.loading = false;
        state.sideWallFrameNumbers = action.payload.data || [];
        console.log('Side wall frame numbers loaded:', state.sideWallFrameNumbers);
      })
      .addCase(fetchSideWallFrameNumbers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('Failed to load side wall frame numbers:', action.payload);
      })
      .addCase(fetchSideWallStageOneForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSideWallStageOneForm.fulfilled, (state, action) => {
        state.loading = false;
        state.sideWallStageOne = action.payload.data;
      })
      .addCase(fetchSideWallStageOneForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteSideWallStageOne.pending, (state) => {
        state.deleteStageOne = { loading: true, success: false, error: null };
      })
      .addCase(deleteSideWallStageOne.fulfilled, (state) => {
        state.deleteStageOne = { loading: false, success: true, error: null };
      })
      .addCase(deleteSideWallStageOne.rejected, (state, action) => {
        state.deleteStageOne = { loading: false, success: false, error: action.payload };
      })
      .addCase(addSideWallStageOne.pending, (state) => {
        state.addStageOne = { loading: true, success: false, error: null };
      })
      .addCase(addSideWallStageOne.fulfilled, (state) => {
        state.addStageOne = { loading: false, success: true, error: null };
      })
      .addCase(addSideWallStageOne.rejected, (state, action) => {
        state.addStageOne = { loading: false, success: false, error: action.payload };
      })
      .addCase(fetchSideWallStageOneList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSideWallStageOneList.fulfilled, (state, action) => {
        state.loading = false;
        state.stageOneList = action.payload?.data;
        state.pagination = {
          total: action.payload.total,
          per_page: action.payload.per_page,
          current_page: action.payload.current_page,
          last_page: action.payload.last_page,
        };
      })
      .addCase(fetchSideWallStageOneList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===============================
      // FETCH STAGE-3 FORM - FIXED
      // ===============================
      .addCase(fetchSideWallStageThreeForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSideWallStageThreeForm.fulfilled, (state, action) => {
        state.loading = false;
        // Set BOTH properties so component can access the data
        state.stageThreeFormData = action.payload.data;
        state.sideWallStageThree = action.payload.data; // This is what your component uses
      })
      .addCase(fetchSideWallStageThreeForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===============================
      // ADD STAGE-3
      // ===============================
      .addCase(addSideWallStageThree.pending, (state) => {
        state.addStageThree = {
          loading: true,
          success: false,
          error: null,
        };
      })
      .addCase(addSideWallStageThree.fulfilled, (state) => {
        state.addStageThree = {
          loading: false,
          success: true,
          error: null,
        };
      })
      .addCase(addSideWallStageThree.rejected, (state, action) => {
        state.addStageThree = {
          loading: false,
          success: false,
          error: action.payload,
        };
      })

      // ===============================
      // FETCH STAGE-3 LIST
      // ===============================
      .addCase(fetchSideWallStageThreeList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSideWallStageThreeList.fulfilled, (state, action) => {
        state.loading = false;
        state.stageThreeList = action.payload?.data;
        state.stageThreePagination = {
          total: action.payload.total,
          per_page: action.payload.per_page,
          current_page: action.payload.current_page,
          last_page: action.payload.last_page,
        };
      })
      .addCase(fetchSideWallStageThreeList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteSideWallStageThree.pending, (state) => {
        state.deleteStageThree = {
          loading: true,
          success: false,
          error: null,
        };
      })
      .addCase(deleteSideWallStageThree.fulfilled, (state) => {
        state.deleteStageThree = {
          loading: false,
          success: true,
          error: null,
        };
      })
      .addCase(deleteSideWallStageThree.rejected, (state, action) => {
        state.deleteStageThree = {
          loading: false,
          success: false,
          error: action.payload,
        };
      })
      .addCase(fetchSideWallStageTwoForm.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSideWallStageTwoForm.fulfilled, (state, action) => {
        state.loading = false;
        state.stageTwoData = action.payload.data;
      })
      .addCase(fetchSideWallStageTwoForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchmetaltypeFrameNumbers.fulfilled, (state, action) => {
        state.metalframeNumbers = action.payload.data;
      })
      .addCase(fetchSideWallStageTwoList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSideWallStageTwoList.fulfilled, (state, action) => {
        state.loading = false;
        state.stageTwoList = action.payload.data;
        state.pagination = {
          total: action.payload.total,
          per_page: action.payload.per_page,
          current_page: action.payload.current_page,
          last_page: action.payload.last_page,
        };
      })
      .addCase(fetchSideWallStageTwoList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addSideWallStageTwo.pending, (state) => {
        state.addStageTwo = { loading: true, success: false, error: null };
      })
      .addCase(addSideWallStageTwo.fulfilled, (state) => {
        state.addStageTwo = { loading: false, success: true, error: null };
      })
      .addCase(addSideWallStageTwo.rejected, (state, action) => {
        state.addStageTwo = { loading: false, success: false, error: action.payload };
      })
      .addCase(sideWallActionStageOne.pending, (state) => {
        state.loading = true;
      })
      .addCase(sideWallActionStageOne.fulfilled, (state, action) => {
        state.loading = false;
        state.actionStageOne = action.payload.data;
      })
      .addCase(sideWallActionStageOne.rejected, (state, action) => {
        state.loading = false;
        state.actionStageOne = action.payload.data;
      })
      .addCase(fetchSideWallFlowHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSideWallFlowHistory.fulfilled, (state, action) => {
        state.flowHistory = action.payload.data || [];
      })
      .addCase(fetchSideWallFlowHistory.rejected, (state, action) => {
        state.loading = false;
        state.flowHistory = action.payload.data;
      })

      .addCase(fetchViewSideWallStageOne.pending, (state) => {
        state.viewStageOne.loading = true;
        state.viewStageOne.error = null;
      })
      .addCase(fetchViewSideWallStageOne.fulfilled, (state, action) => {
        state.viewStageOne.loading = false;
        state.viewStageOne.success = true;
        state.viewStageOne.data = action.payload.data;
      })
      .addCase(fetchViewSideWallStageOne.rejected, (state, action) => {
        state.viewStageOne.loading = false;
        state.viewStageOne.success = false;
        state.viewStageOne.error = action.payload || 'Failed to fetch flow history';
      })

      .addCase(fetchViewSideWallStageTwo.pending, (state) => {
        state.viewStageTwo.loading = true;
        state.viewStageTwo.error = null;
      })
      .addCase(fetchViewSideWallStageTwo.fulfilled, (state, action) => {
        state.viewStageTwo.loading = false;
        state.viewStageTwo.success = true;
        state.viewStageTwo.data = action.payload.data;
      })
      .addCase(fetchViewSideWallStageTwo.rejected, (state, action) => {
        state.viewStageTwo.loading = false;
        state.viewStageTwo.success = false;
        state.viewStageTwo.error = action.payload || 'Failed to fetch flow history';
      })

      .addCase(fetchViewSideWallStageThree.pending, (state) => {
        state.viewStageThree.loading = true;
        state.viewStageThree.error = null;
      })
      .addCase(fetchViewSideWallStageThree.fulfilled, (state, action) => {
        state.viewStageThree.loading = false;
        state.viewStageThree.success = true;
        state.viewStageThree.data = action.payload.data;
      })
      .addCase(fetchViewSideWallStageThree.rejected, (state, action) => {
        state.viewStageThree.loading = false;
        state.viewStageThree.success = false;
        state.viewStageThree.error = action.payload || 'Failed to fetch flow history';
      })
      .addCase(fetchSideWallFlowHistoryStageOne.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSideWallFlowHistoryStageOne.fulfilled, (state, action) => {
        state.flowHistoryStageOne = action.payload.data || [];
      })
      .addCase(fetchSideWallFlowHistoryStageOne.rejected, (state, action) => {
        state.loading = false;
        state.flowHistoryStageOne = action.payload.data;
      })
      .addCase(fetchSideWallFlowHistoryStageTwo.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSideWallFlowHistoryStageTwo.fulfilled, (state, action) => {
        state.flowHistoryStageTwo = action.payload.data || [];
      })
      .addCase(fetchSideWallFlowHistoryStageTwo.rejected, (state, action) => {
        state.loading = false;
        state.flowHistoryStageTwo = action.payload.data;
      })
      .addCase(fetchSideWallFlowHistoryStageThree.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSideWallFlowHistoryStageThree.fulfilled, (state, action) => {
        state.flowHistoryStageThree = action.payload.data || [];
      })
      .addCase(fetchSideWallFlowHistoryStageThree.rejected, (state, action) => {
        state.loading = false;
        state.flowHistoryStageThree = action.payload.data;
      });
  },
});

export const { clearMessages } = sideWallSlice.actions;
export default sideWallSlice.reducer;
